export const ITEMIZED_TARIFF_MODE = "itemized";
export const SINGLE_ENERGY_RATE = "single";

export function legacyFixedChargeErrors(tariffs) {
  const details = tariffs?.legacyFixed || {};
  return ["fixedMonthlyEur", "otherFixedMonthlyEur"].filter(key => {
    const raw = details[key];
    if (raw === "" || raw == null) return false;
    const value = Number(raw);
    return !Number.isFinite(value) || value < 0;
  });
}

const numberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const valueNumber = Number(value);
  return Number.isFinite(valueNumber) ? valueNumber : null;
};

const recoverableVatPct = (value) =>
  value === "" || value === null || value === undefined ? 0 : numberOrNull(value);

export function itemizedTariffErrors(tariffs, months) {
  if (tariffs?.rateMode !== ITEMIZED_TARIFF_MODE) return [];
  const details = tariffs.itemized || {};
  const errors = [];
  for (const month of months) {
    const rates = details.monthly?.[month] || {};
    for (const band of details.pricingMode === SINGLE_ENERGY_RATE ? ["peak"] : ["peak", "offPeak"]) {
      const rate = numberOrNull(rates[band]);
      if (rate === null || rate < 0) errors.push(`${month}.${band}`);
    }
  }
  for (const [key, max] of [["regulatedEurKwh", null], ["vatRatePct", 100], ["vatRecoverablePct", 100]]) {
    const value = key === "vatRecoverablePct" ? recoverableVatPct(details[key]) : numberOrNull(details[key]);
    if (value === null || value < 0 || (max !== null && value > max)) errors.push(key);
  }
  for (const key of ["fixedMonthlyEur", "otherFixedMonthlyEur"]) {
    const raw = details[key];
    if (raw !== "" && raw !== null && raw !== undefined) {
      const amount = numberOrNull(raw);
      if (amount === null || amount < 0) errors.push(key);
    }
  }
  const yko = details.ykoEurKwh;
  if (yko !== "" && yko !== null && yko !== undefined) {
    const amount = numberOrNull(yko);
    if (amount === null || amount < 0) errors.push("ykoEurKwh");
  }
  return errors;
}

export function itemizedTariffRate(tariffs, month, band) {
  const details = tariffs?.itemized || {};
  const supply = numberOrNull(details.monthly?.[month]?.[details.pricingMode === SINGLE_ENERGY_RATE ? "peak" : band]);
  const regulated = numberOrNull(details.regulatedEurKwh);
  const yko = details.ykoEurKwh === "" || details.ykoEurKwh == null ? 0 : numberOrNull(details.ykoEurKwh);
  const vat = numberOrNull(details.vatRatePct);
  const recovered = recoverableVatPct(details.vatRecoverablePct);
  if ([supply, regulated, yko, vat, recovered].some((value) => value === null)) return null;
  if (supply < 0 || regulated < 0 || yko < 0 || vat < 0 || vat > 100 || recovered < 0 || recovered > 100) return null;
  const beforeVat = supply + regulated + yko;
  const nonRecoverableVat = beforeVat * (vat / 100) * (1 - recovered / 100);
  return { supply, regulated, yko, nonRecoverableVat, effective: beforeVat + nonRecoverableVat };
}

export function itemizedYkoRate(tariffs) {
  const details = tariffs?.itemized || {};
  const yko = details.ykoEurKwh === "" || details.ykoEurKwh === null || details.ykoEurKwh === undefined
    ? 0 : numberOrNull(details.ykoEurKwh);
  const vat = numberOrNull(details.vatRatePct);
  const recovered = recoverableVatPct(details.vatRecoverablePct);
  if ([yko, vat, recovered].some((value) => value === null)) return null;
  if (yko < 0 || vat < 0 || vat > 100 || recovered < 0 || recovered > 100) return null;
  return yko * (1 + vat / 100 * (1 - recovered / 100));
}

export function itemizedFixedMonthlyCost(tariffs) {
  const details = tariffs?.itemized || {};
  const optionalCost = (raw) => (raw === "" || raw === null || raw === undefined ? 0 : numberOrNull(raw));
  const supplier = optionalCost(details.fixedMonthlyEur);
  const other = optionalCost(details.otherFixedMonthlyEur);
  const vat = numberOrNull(details.vatRatePct);
  const recovered = recoverableVatPct(details.vatRecoverablePct);
  if (![supplier, other, vat, recovered].every(Number.isFinite)) return null;
  if (supplier < 0 || other < 0 || vat < 0 || vat > 100 || recovered < 0 || recovered > 100) return null;
  return supplier * (1 + vat / 100 * (1 - recovered / 100)) + other;
}
